/**
 * Pure mappers from a selected imported datum to the fields the Propose-correction
 * form needs. Kept separate from the picker UI so the source_identifier / period /
 * value derivation is unit-testable.
 *
 * Values stay canonical decimal strings throughout and are never `Number()`'d
 * (locked decision #6: the UI never recomputes a compliance figure). The one
 * arithmetic step is undoing the series endpoint's display conversion, which is
 * an exact Decimal multiply by the divisor that response carries — see
 * `toNativeValue`.
 */
import { Decimal } from "@compliance/domain";
import type { SeriesPoint, SeriesResponse, UploadRowEntry } from "../../api/types";
import type { CellCorrection } from "../../components/data/correctionOverlay";
import { formatPacificDisplay } from "../../lib/datetime";
import { ISO_DAY_PREFIX_RE, ISO_DAY_RE } from "../../lib/uploadColumns";

// The upload-column heuristics are shared with UploadRowsDrawer via one module
// so the date-column detection can't drift between the two surfaces (BIO-144 #3).
export {
  deriveColumns,
  detectDateColumn,
  detectNumericColumns,
  looksDate,
  looksNumeric,
} from "../../lib/uploadColumns";

export type PickedDatum = {
  /** `{facility}:{role}` for a DUT series, `{upload_type}:row-N` for an upload row. */
  source_identifier: string;
  /** PT gas-day (YYYY-MM-DD) when known — used for the label. */
  day: string | null;
  /**
   * Pacific wall-clock datetime-local ("YYYY-MM-DDTHH:mm") for the correction's
   * period_start: gas-day midnight for a daily pick, the exact hour for an hourly
   * pick. `null` when unknown (e.g. an upload row with no parseable date).
   */
  period_local: string | null;
  /** Canonical decimal string (the figure exactly as the backend serialized it). */
  value: string;
  /** Short human label for the "filled from imported data" indicator. */
  label: string;
  /**
   * Set when the picked value is the effective value of an APPROVED correction
   * — the operator is correcting a correction. `db_value` is the original
   * stored value that correction replaced/excluded.
   */
  correctedBy?: { correction_id: string; db_value: string | null } | null;
  /** Pending proposals already targeting this datum (duplicate-proposal warning). */
  pendingCorrectionIds?: string[];
};

/**
 * Layer the cell's correction state onto a picked datum. An approved
 * substitution swaps in its `after_value` — the currently-EFFECTIVE figure —
 * so the new correction's `before_value` is the corrected value it supersedes,
 * not the stale db reading. An approved exclusion has no after-value, so the
 * db reading stays the "before" of a correction on an excluded cell. Pending
 * proposals only annotate (they never change the effective value).
 */
export function withCorrectionContext(
  d: PickedDatum,
  cell: CellCorrection | undefined,
): PickedDatum {
  if (cell == null) return d;
  const pendingIds = cell.pending.map((p) => p.correction_id);
  const out: PickedDatum = { ...d };
  if (pendingIds.length > 0) out.pendingCorrectionIds = pendingIds;
  const approved = cell.approved;
  if (approved == null) return out;
  out.correctedBy = { correction_id: approved.correction_id, db_value: d.value };
  if (cell.excluded) {
    out.label = `${d.label} · excluded`;
  } else if (cell.effectiveValue != null) {
    out.value = cell.effectiveValue;
    out.label = `${d.label} · corrected`;
  }
  return out;
}

/**
 * Undo the series endpoint's display conversion, exactly.
 *
 * Decimal, never `Number`: these are compliance figures, and a float round-trip
 * on a nine-significant-digit SCF reading is not exact. Returns the input
 * unchanged when the divisor is 1, so roles the reader already serves in their
 * display unit keep their literal string.
 */
function toNativeValue(displayed: string, divisor: string): string {
  if (divisor === "1") return displayed;
  return new Decimal(displayed).times(new Decimal(divisor)).toFixed();
}

/**
 * A DUT daily-series point → a picked datum. The source id is `{facility}:{role}`,
 * matching the loader/issues format (omai issues/loader.py `_series`), so a
 * correction lines up with the same source the validation findings reference.
 *
 * **The value is converted back to the source's NATIVE unit.** The series is
 * served in display units — every volume in MSCF — but a correction is stored in
 * `corrections.before_value`/`after_value` and read back by the applier and by
 * validation, both of which work in the unit the backend reader emits (SCF for
 * the two hub volume roles). Storing the displayed figure would record every hub
 * volume substitution a thousandfold too small, silently, in the inputs to a
 * filed figure. `native_divisor` is the exact factor to undo it.
 */
export function pickFromSeries(resp: SeriesResponse, point: SeriesPoint): PickedDatum {
  const sourceId = `${resp.facility_identifier}:${resp.role}`;
  // The bucket-start is a tz-aware instant; render it in Pacific to get both the
  // gas-day (leading YYYY-MM-DD) and the wall-clock period to fill. For an hourly
  // pick this carries the hour; for daily it lands on PT midnight.
  const ptIso = formatPacificDisplay(point.bucket_start);
  const periodLocal = ptIso ? ptIso.slice(0, 16) : null; // "YYYY-MM-DDTHH:mm"
  const day = (ptIso ?? point.bucket_start).slice(0, 10);
  return {
    source_identifier: sourceId,
    day: ISO_DAY_RE.test(day) ? day : null,
    period_local: periodLocal,
    value: toNativeValue(point.value, resp.native_divisor),
    label: `${sourceId} · ${periodLocal ? periodLocal.replace("T", " ") : day}`,
  };
}

/**
 * An upload row + chosen value column → a picked datum. There is no native
 * `{facility}:{role}` id for uploads, so we use the `{upload_type}:row-N`
 * convention (matches the form's existing placeholder, e.g. `pge_statement:row-42`).
 */
export function pickFromUploadRow(
  uploadType: string,
  row: UploadRowEntry,
  valueColumn: string,
  dateColumn: string | null,
): PickedDatum {
  const raw = row.data[valueColumn];
  const rawDay = dateColumn ? String(row.data[dateColumn] ?? "") : "";
  const day = ISO_DAY_PREFIX_RE.test(rawDay) ? rawDay.slice(0, 10) : null;
  return {
    source_identifier: `${uploadType}:row-${row.row_number}`,
    day,
    period_local: day ? `${day}T00:00` : null,
    value: raw == null ? "" : String(raw),
    label: `${uploadType} row ${row.row_number} · ${valueColumn}`,
  };
}

/**
 * Gas-day → the native `<input type="datetime-local">` value the propose form's
 * Pacific-wall-time→instant path (`pacificLocalToInstant`) expects. Midnight PT
 * of the gas-day; the backend then counts that single PT day for the threshold.
 */
export function dayToPeriodLocal(day: string): string {
  return `${day}T00:00`;
}

/**
 * A gas-day in the batch window the DUT series returned NO point for → a picked
 * datum carrying a canonical `"0"`, so the operator can substitute a truly-missing
 * day. The series omits empty gas-days, so the daily picker synthesizes them.
 * Shaped like `pickFromSeries`: same `{facility}:{role}` id and PT-midnight period.
 * `"0"` is a literal canonical string — never `Number()`'d (locked decision #6).
 */
export function pickFromMissingDay(resp: SeriesResponse, day: string): PickedDatum {
  const sourceId = `${resp.facility_identifier}:${resp.role}`;
  return {
    source_identifier: sourceId,
    day,
    period_local: dayToPeriodLocal(day),
    value: "0",
    label: `${sourceId} · ${day} · no data`,
  };
}
